import {
  IAttributeRegistry,
  IGovernanceStructure,
  KeyPersonRoleType,
  ROLE_ORDER,
  useCreateAttributePropertyOption,
  useGetAttributeProperties,
} from '@commenda/api';
import {
  Button,
  Checkbox,
  CountryCode,
  DatePicker,
  FieldWrapper,
  FlagComponent,
  IconButton,
  Icons,
  Input,
  Label,
  Sheet,
  SheetContent,
  SingleCombobox,
  Tooltip,
} from '@commenda/kakashi';
import { formatIsoDateShort } from '@commenda/utils';
import { useMemo, useState } from 'react';
import { ConfirmationModal } from '../../../molecules/modals/ConfirmationModal';
import {
  CommittedField,
  CustomFieldsBuilder,
  customFieldsHaveMissingRequired,
  valueIsEmpty,
  type FieldDefinition,
} from '../CustomFieldsBuilder';
import {
  GOVERNANCE_ROLE_OPTIONS,
  OFFICER_ROLE_OPTIONS,
  ROLE_CARD_TITLE,
  ROLE_DESCRIPTIONS,
  ROLE_DETAIL_LABEL,
  ROLE_NOUN,
  UBO_CONTROL_TYPE_OPTIONS,
} from './constants';
import type { AddedRole, ConfigDraft, SharedField } from './types';
import {
  isValidEmail,
  resolveRoleOptions,
  roleSummary,
  toBuilderFieldType,
  withLocalOfficeholderTerm,
} from './utils';

// Director is offered as a governance role instead; it stays in ROLE_ORDER for read surfaces.
const ASSIGNABLE_ROLE_SECTIONS = ROLE_ORDER.filter(
  (roleType) => roleType !== 'DIRECTOR'
);

export const EntityFlag = ({
  country,
  name,
  size = 'sm',
}: {
  country: string;
  name?: string;
  size?: 'sm' | 'md';
}) => (
  <span
    className={`inline-flex ${
      size === 'md' ? 'size-6' : 'size-5'
    } shrink-0 overflow-hidden rounded-full`}
  >
    <FlagComponent
      country={country as CountryCode}
      countryName={name}
      _className="size-full"
    />
  </span>
);

// `roleType` lingers after close so the body keeps rendering through the slide-out.
export const RoleConfigSheet = ({
  open,
  roleType,
  entityName,
  selectedCount,
  isEditing,
  companyId,
  draft,
  onPatch,
  onClose,
  onAdd,
  sharedFields,
  onAddSharedField,
  onRemoveSharedField,
  allowedGovernanceRoles,
  governanceStructure,
  governanceSeatRole,
  applyToAllDisabledReason,
  datesOnly = false,
  lockedDetail = null,
  lockEndDate = false,
}: {
  open: boolean;
  roleType: KeyPersonRoleType | null;
  entityName: string;
  selectedCount: number;
  isEditing: boolean;
  companyId: number;
  draft: ConfigDraft;
  onPatch: (patch: Partial<ConfigDraft>) => void;
  onClose: () => void;
  onAdd: () => void;
  sharedFields: SharedField[];
  onAddSharedField: (definition: FieldDefinition) => void;
  onRemoveSharedField: (key: string) => void;
  // One ⇒ fixed; several ⇒ a picker; none ⇒ unavailable.
  allowedGovernanceRoles: string[];
  governanceStructure?: IGovernanceStructure;
  governanceSeatRole?: string;
  // The copy is the tooltip on the disabled "apply to all".
  applyToAllDisabledReason: string | null;
  // Existing assignment: only the dates are writable; the role's chosen value is shown read-only.
  datesOnly?: boolean;
  lockedDetail?: string | null;
  lockEndDate?: boolean;
}) => {
  const { data: registry } = useGetAttributeProperties(companyId, {
    enabled: open,
  });
  const officerRoleOptions = useMemo(
    () =>
      resolveRoleOptions(
        registry,
        'OFFICER',
        'officerRole',
        OFFICER_ROLE_OPTIONS
      )
        // "Other" is replaced by the inline "add custom role" input; drop it from the picker.
        .filter((option) => option.value !== 'Other'),
    [registry]
  );
  const governanceRoleOptions = useMemo(
    () =>
      withLocalOfficeholderTerm(
        resolveRoleOptions(
          registry,
          'GOVERNANCE',
          'governanceRole',
          GOVERNANCE_ROLE_OPTIONS
        ).filter((option) => allowedGovernanceRoles.includes(option.value)),
        governanceStructure,
        governanceSeatRole
      ),
    [registry, allowedGovernanceRoles, governanceStructure, governanceSeatRole]
  );
  const controlTypeOptions = useMemo(
    () =>
      resolveRoleOptions(
        registry,
        'UBO',
        'controlType',
        UBO_CONTROL_TYPE_OPTIONS
      ),
    [registry]
  );

  // Inline "add custom officer role" — persists a company-scoped ENUM option so
  // the assign call (which validates against allowed options) accepts it.
  const createOfficerOption = useCreateAttributePropertyOption();

  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const handleAddCustomOfficer = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    // On failure the error surfaces via createOfficerOption.error below.
    createOfficerOption.mutate(
      {
        companyId,
        attributeKey: 'OFFICER',
        propertyKey: 'officerRole',
        label: trimmed,
      },
      { onSuccess: (option) => onPatch({ officerRole: option.value }) }
    );
  };

  // Custom properties already registered on this role's attribute (company-wide),
  // surfaced so required ones can be filled — their values flow via customFields.
  const customProps = useMemo(() => {
    if (!roleType) return [];
    const attribute = registry?.attributes.find((a) => a.key === roleType);
    return (attribute?.properties ?? []).filter(
      (p) => p.isCustom && !p.isOneOff
    );
  }, [registry, roleType]);

  const missingRequiredCustom = customProps.some(
    (p) =>
      p.isRequired && valueIsEmpty(p.type, draft.customValues[p.unqualifiedKey])
  );

  const missingRequiredShared = sharedFields.some(
    (f) => f.isRequired && valueIsEmpty(f.type, draft.customValues[f.key])
  );

  const endBeforeStart =
    !!draft.startDate && !!draft.endDate && draft.endDate < draft.startDate;

  const canAdd =
    !!draft.startDate &&
    !endBeforeStart &&
    (datesOnly ||
      (!missingRequiredCustom &&
        !missingRequiredShared &&
        (roleType !== 'OFFICER' || !!draft.officerRole) &&
        (roleType !== 'GOVERNANCE' || !!draft.governanceRole) &&
        (roleType !== 'UBO' || !!draft.controlType) &&
        (!draft.roleEmail || isValidEmail(draft.roleEmail)) &&
        !customFieldsHaveMissingRequired(draft.customFields)));

  // What the user typed, as opposed to what the parent seeded: `startDate` is always pre-filled
  // and a lone allowed governance role is chosen for them, so neither means there is work to lose.
  const hasInput =
    !datesOnly &&
    (!!draft.officerRole ||
      !!draft.roleEmail ||
      !!draft.controlType ||
      !!draft.endDate ||
      draft.applyToAll ||
      (allowedGovernanceRoles.length > 1 && !!draft.governanceRole) ||
      draft.customFields.length > 0 ||
      Object.keys(draft.customValues).length > 0);

  // Closing drops the draft for good — the parent reseeds it on the next open — so every way
  // out but the drag asks first once there is something to lose.
  const requestClose = () => {
    if (hasInput) setConfirmingDiscard(true);
    else onClose();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // Escape, the backdrop and the close icon are all taken below, so the only gesture
        // still arriving here is the drag — deliberate enough to discard without asking.
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="bottom"
        srTitle="Assign role"
        // The sheet's own icon closes through onOpenChange, which cannot tell it apart from
        // the drag; ours routes through requestClose instead.
        hideCloseIcon
        onEscapeKeyDown={(event) => {
          // preventDefault both stops Radix dismissing the sheet from under the confirmation
          // and marks the key handled, which is what keeps the wizard behind it open.
          event.preventDefault();
          requestClose();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
          requestClose();
        }}
      >
        <IconButton
          icon={<Icons.XClose className="size-5" />}
          aria-label="Close"
          variant="default"
          size="md"
          className="absolute right-xl top-xl z-10"
          onClick={requestClose}
        />
        {roleType && (
          <div className="flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto overscroll-contain px-xl pb-8xl pt-4xl">
            <div className="flex w-full max-w-xl flex-col gap-5xl">
              <div className="flex flex-col gap-4xl">
                <h2 className="display-xs-bold text-text-primary">
                  {isEditing ? 'Update' : 'Assign'} {ROLE_NOUN[roleType]} role
                  for {entityName}
                </h2>
                <div className="flex flex-col gap-3xl">
                  <div className="flex flex-col gap-3xl rounded-md border border-border-secondary bg-bg-primary p-3xl">
                    {datesOnly && lockedDetail && (
                      <FieldWrapper>
                        <Label>{ROLE_DETAIL_LABEL[roleType]}</Label>
                        <Input value={lockedDetail} disabled readOnly />
                      </FieldWrapper>
                    )}
                    {!datesOnly && roleType === 'OFFICER' && (
                      <>
                        <FieldWrapper>
                          <Label required>Officer role</Label>
                          <SingleCombobox
                            options={officerRoleOptions}
                            value={draft.officerRole ?? ''}
                            setValue={(v) => onPatch({ officerRole: v })}
                            placeholder="Select officer role"
                            searchPlaceholder="Search or add a role"
                            addItemConfig={{
                              shouldAllowAdd: true,
                              createFromSearch: true,
                              onAddItem: handleAddCustomOfficer,
                              placeholder: 'Type a role name to add it',
                            }}
                          />
                          {createOfficerOption.isError && (
                            <span className="txt-extra-small-regular text-text-error">
                              {createOfficerOption.error instanceof Error
                                ? createOfficerOption.error.message
                                : 'Failed to add role.'}
                            </span>
                          )}
                        </FieldWrapper>
                        <FieldWrapper>
                          <Label>Role email (optional)</Label>
                          <Input
                            type="email"
                            value={draft.roleEmail ?? ''}
                            onChange={(e) =>
                              onPatch({ roleEmail: e.target.value })
                            }
                            placeholder="name@example.com"
                          />
                          {draft.roleEmail &&
                            !isValidEmail(draft.roleEmail) && (
                              <span className="txt-extra-small-regular text-text-error">
                                Enter a valid email address.
                              </span>
                            )}
                        </FieldWrapper>
                      </>
                    )}
                    {!datesOnly && roleType === 'GOVERNANCE' && (
                      <FieldWrapper>
                        <Label required>Governance role</Label>
                        {allowedGovernanceRoles.length === 0 ? (
                          <Tooltip content="Governance roles aren't available for this entity type.">
                            <div>
                              <SingleCombobox
                                options={[]}
                                value=""
                                setValue={() => {}}
                                disabled
                                placeholder="Not available"
                              />
                            </div>
                          </Tooltip>
                        ) : (
                          <SingleCombobox
                            options={governanceRoleOptions}
                            value={draft.governanceRole ?? ''}
                            setValue={(v) => onPatch({ governanceRole: v })}
                            placeholder="Select governance role"
                            disabled={allowedGovernanceRoles.length === 1}
                          />
                        )}
                        {governanceStructure && (
                          <p className="txt-small-regular text-text-secondary">
                            Governed by the{' '}
                            {governanceStructure.bodyLocalTerm.replace(
                              /^the\s+/i,
                              ''
                            )}
                            {governanceStructure.minMembers !== null &&
                              governanceStructure.minMembers > 1 &&
                              ` — at least ${governanceStructure.minMembers} members`}
                            .
                          </p>
                        )}
                      </FieldWrapper>
                    )}
                    {!datesOnly && roleType === 'UBO' && (
                      <FieldWrapper>
                        <Label required>Control type</Label>
                        <SingleCombobox
                          options={controlTypeOptions}
                          value={draft.controlType ?? ''}
                          setValue={(v) => onPatch({ controlType: v })}
                          placeholder="Select control type"
                        />
                      </FieldWrapper>
                    )}
                    <FieldWrapper>
                      <Label required>Start date</Label>
                      <DatePicker
                        date={draft.startDate}
                        setDate={(d) => onPatch({ startDate: d || undefined })}
                        placeholder="Select date"
                      />
                    </FieldWrapper>
                    <FieldWrapper>
                      <Label>End date (optional)</Label>
                      <DatePicker
                        date={draft.endDate}
                        setDate={(d) => onPatch({ endDate: d || undefined })}
                        placeholder="Select date"
                        disabled={lockEndDate}
                      />
                      {lockEndDate && (
                        <span className="txt-extra-small-regular text-text-tertiary">
                          UBO roles are ended from the role menu so ownership
                          records stay in sync.
                        </span>
                      )}
                      {endBeforeStart && (
                        <span className="txt-extra-small-regular text-text-error">
                          End date can&apos;t be before the start date.
                        </span>
                      )}
                    </FieldWrapper>
                    {selectedCount > 1 &&
                      !isEditing &&
                      (applyToAllDisabledReason ? (
                        <Tooltip content={applyToAllDisabledReason}>
                          <label className="flex w-fit items-center gap-md opacity-60">
                            <Checkbox checked={false} disabled />
                            <span className="txt-small-regular text-text-secondary">
                              Apply role to all selected entities
                            </span>
                          </label>
                        </Tooltip>
                      ) : (
                        <label className="flex items-center gap-md">
                          <Checkbox
                            checked={draft.applyToAll}
                            onCheckedChange={(v) =>
                              onPatch({ applyToAll: v === true })
                            }
                          />
                          <span className="txt-small-regular text-text-secondary">
                            Apply role to all selected entities
                          </span>
                        </label>
                      ))}
                  </div>
                  {!datesOnly &&
                    customProps.map((prop) => (
                      <CommittedField
                        key={prop.id}
                        field={{
                          id: prop.id,
                          type: toBuilderFieldType(prop.type),
                          label: prop.label,
                          isRequired: prop.isRequired,
                          saveForFuture: true,
                          value: draft.customValues[prop.unqualifiedKey],
                        }}
                        companyId={companyId}
                        onChange={(patch) =>
                          onPatch({
                            customValues: {
                              ...draft.customValues,
                              [prop.unqualifiedKey]: patch.value,
                            },
                          })
                        }
                      />
                    ))}
                  {!datesOnly &&
                    sharedFields.map((field) => (
                      <CommittedField
                        key={field.key}
                        field={{
                          id: field.key,
                          type: field.type,
                          label: field.label,
                          isRequired: field.isRequired,
                          saveForFuture: true,
                          value: draft.customValues[field.key],
                        }}
                        companyId={companyId}
                        onChange={(patch) =>
                          onPatch({
                            customValues: {
                              ...draft.customValues,
                              [field.key]: patch.value,
                            },
                          })
                        }
                        onRemove={() => onRemoveSharedField(field.key)}
                      />
                    ))}
                  {!datesOnly && (
                    <CustomFieldsBuilder
                      fields={draft.customFields}
                      onChange={(customFields) => onPatch({ customFields })}
                      companyId={companyId}
                      onAddSharedField={onAddSharedField}
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end gap-xl">
                <Button variant="secondary" onClick={requestClose}>
                  Cancel
                </Button>
                <Button onClick={onAdd} disabled={!canAdd}>
                  {isEditing ? 'Update' : 'Assign'} {ROLE_NOUN[roleType]} role
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
      <ConfirmationModal
        isOpen={confirmingDiscard}
        onClose={() => setConfirmingDiscard(false)}
        onConfirm={() => {
          setConfirmingDiscard(false);
          onClose();
        }}
        tone="destructive"
        title="Discard this role?"
        message="The details you entered here will not be saved."
        confirmButtonText="Discard"
        cancelButtonText="Keep editing"
      />
    </Sheet>
  );
};

export const EntityRoleCard = ({
  entityName,
  country,
  addedRoles,
  onAddRole,
  onRemoveRole,
  onEditRole,
  roleErrors,
  incompleteIds,
  registry,
  governanceDisabled,
  hideGovernance,
}: {
  entityName: string;
  country?: string | null;
  addedRoles: AddedRole[];
  onAddRole: (roleType: KeyPersonRoleType) => void;
  onRemoveRole: (id: string) => void;
  onEditRole: (role: AddedRole) => void;
  roleErrors: Record<string, string>;
  incompleteIds: Set<string>;
  registry: IAttributeRegistry | undefined;
  governanceDisabled: boolean;
  // Temporary (FE-only): when true, the governance section is hidden entirely (partnerships, for now).
  hideGovernance: boolean;
}) => {
  const [expanded, setExpanded] = useState(true);
  const roleSections = hideGovernance
    ? ASSIGNABLE_ROLE_SECTIONS.filter((roleType) => roleType !== 'GOVERNANCE')
    : ASSIGNABLE_ROLE_SECTIONS;
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border-primary bg-bg-primary">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center justify-between gap-md border-b border-border-primary p-3xl"
      >
        <div className="flex min-w-0 items-center gap-md">
          {country && (
            <EntityFlag country={country} name={entityName} size="md" />
          )}
          <p className="truncate txt-large-bold text-text-primary">
            {entityName}
          </p>
        </div>
        <Icons.ChevronDown
          className={`size-4 shrink-0 text-text-quaternary transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && (
        <div className="flex flex-col gap-3xl p-3xl">
          {roleSections.map((roleType) => {
            const instances = addedRoles.filter((r) => r.roleType === roleType);
            return (
              <div key={roleType} className="flex flex-col gap-xl">
                <div className="flex flex-col gap-xxs">
                  <p className="txt-medium-bold text-text-primary">
                    {ROLE_CARD_TITLE[roleType]}
                  </p>
                  <p className="txt-small-regular text-text-secondary">
                    {ROLE_DESCRIPTIONS[roleType]}
                  </p>
                </div>
                {instances.length > 0 && (
                  <div className="flex flex-col gap-md">
                    {instances.map((r) => {
                      const roleError = roleErrors[r.id];
                      const incomplete = incompleteIds.has(r.id);
                      const flagged = Boolean(roleError) || incomplete;
                      return (
                        <div key={r.id} className="flex flex-col gap-xs">
                          <div
                            className={`flex items-center justify-between rounded-md border-[0.5px] bg-bg-light py-xl pl-xl pr-3xl ${
                              flagged
                                ? 'border-border-error'
                                : 'border-border-primary'
                            }`}
                          >
                            <div className="flex flex-col gap-xxs">
                              <p className="txt-small-bold text-text-primary">
                                {roleSummary(r, registry)}
                              </p>
                              {formatIsoDateShort(r.startDate) && (
                                <p className="txt-extra-small-regular text-text-secondary">
                                  Effective {formatIsoDateShort(r.startDate)}
                                  {formatIsoDateShort(r.endDate)
                                    ? ` – ${formatIsoDateShort(r.endDate)}`
                                    : ''}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-xs">
                              <button
                                type="button"
                                aria-label="Remove role"
                                onClick={() => onRemoveRole(r.id)}
                                className="flex items-center rounded-xs p-md text-text-quaternary hover:text-text-error"
                              >
                                <Icons.Trash01 className="size-4" />
                              </button>
                              <button
                                type="button"
                                aria-label="Edit role"
                                onClick={() => onEditRole(r)}
                                className="flex items-center rounded-xs p-md text-text-quaternary hover:text-text-primary"
                              >
                                <Icons.Edit02 className="size-4" />
                              </button>
                            </div>
                          </div>
                          {roleError ? (
                            <span className="txt-extra-small-regular text-text-error">
                              Couldn&apos;t be assigned — {roleError}.
                            </span>
                          ) : incomplete ? (
                            <span className="txt-extra-small-regular text-text-error">
                              Missing a required field — edit to complete.
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
                {roleType === 'GOVERNANCE' && governanceDisabled ? (
                  <Tooltip content="Governance roles aren't available for this entity type.">
                    <div className="w-fit">
                      <Button variant="secondary" size="sm" disabled>
                        <Icons.Plus className="size-4" />
                        <span>Add governance role</span>
                      </Button>
                    </div>
                  </Tooltip>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onAddRole(roleType)}
                  >
                    <Icons.Plus className="size-4" />
                    <span>
                      {roleType === 'OFFICER'
                        ? 'Add officer role(s)'
                        : `Add ${ROLE_NOUN[roleType]} role`}
                    </span>
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
